import { PredictionAdmin, TaskAdmin } from '@gleamer/types';
import classNames from 'classnames';
import React, { useState } from 'react';
import {
  ImportDecision,
  usePredictionImportDecision,
  usePredictionImportPatients,
  usePredictionImportStudies,
} from '../../../../hooks/usePredictionFileImports';
import { ButtonTone, PageButton } from '../../../atoms/PageButton';
import { Card, selectClassName } from '../../../atoms/Surface';
import { formatDateTime } from '../imports/format';
import { Counts, ImportStatusBadge, Pager } from '../tasks/ActivityParts';
import { allowedImportActions } from '../tasks/activity';
import { ImportIssues } from '../tasks/PredictionsSection';

type PredictionImport = TaskAdmin.PredictionImport;
type PredictionProduct = PredictionAdmin.PredictionProduct;

/** Patients read per page of the tree. */
export const TREE_PAGE_SIZE = 25;

const SETTLE: Record<
  TaskAdmin.PredictionImportAction,
  { label: string; tone: ButtonTone; confirm?: string }
> = {
  replay: { label: 'Replay', tone: 'go-outline' },
  accept: {
    label: 'Accept',
    tone: 'caution',
    confirm:
      'Accept the import as it is? Its dropped lines stay out, and it replaces the studies it touched.',
  },
  abandon: {
    label: 'Abandon',
    tone: 'stop',
    confirm: 'Abandon the import? Its predictions go; the product keeps what it showed.',
  },
};

/** A decision's button; a `confirm` text makes the first click ask, inline, before sending. */
function DecisionButton({
  importId,
  decision,
  label,
  tone,
  confirm,
  disabled,
  onDone,
}: {
  importId: number;
  decision: ImportDecision;
  label: string;
  tone: ButtonTone;
  confirm?: string;
  disabled?: boolean;
  onDone?: () => void;
}) {
  const mutation = usePredictionImportDecision(importId);
  const [asking, setAsking] = useState(false);
  const send = () => {
    setAsking(false);
    mutation.mutate(decision, { onSuccess: () => onDone?.() });
  };
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {asking ? (
        <>
          <span className="text-sm">{confirm}</span>
          <PageButton
            tone={tone === 'stop' ? 'stop' : 'primary'}
            onClick={send}
          >
            Confirm {label.toLowerCase()}
          </PageButton>
          <PageButton onClick={() => setAsking(false)}>Cancel</PageButton>
        </>
      ) : (
        <PageButton
          tone={tone}
          onClick={() => (confirm ? setAsking(true) : send())}
          disabled={disabled || mutation.isLoading}
        >
          {mutation.isLoading ? `${label}…` : label}
        </PageButton>
      )}
      {mutation.isError && (
        <span
          role="alert"
          className="text-sm text-dl-danger"
        >
          {(mutation.error as Error).message}
        </span>
      )}
    </span>
  );
}

/** Moving a DONE import to another product: the target picked, then confirmed. */
function ChangeProduct({
  imported,
  products,
}: {
  imported: PredictionImport;
  products: PredictionProduct[];
}) {
  const [target, setTarget] = useState('');
  const others = products.filter(p => p.id !== imported.productId);
  if (others.length === 0) {
    return null;
  }
  const chosen = others.find(p => String(p.id) === target);
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <select
        aria-label="Move to product"
        className={classNames(selectClassName, 'w-48')}
        value={target}
        onChange={e => setTarget(e.target.value)}
      >
        <option value="">Move to…</option>
        {others.map(p => (
          <option
            key={p.id}
            value={p.id}
          >
            {p.name}
          </option>
        ))}
      </select>
      {chosen && (
        <DecisionButton
          importId={imported.id}
          decision={{ type: 'product', productId: chosen.id }}
          label="Change product"
          tone="caution"
          confirm={`Move the import to ${chosen.name}? It replaces that product's older predictions on its studies, and its table is renamed.`}
          onDone={() => setTarget('')}
        />
      )}
    </span>
  );
}

/** What the tree's Preview links open: the import's predictions, and the product's current ones; null when the viewer cannot preview. */
type Previews = { importId: number; productId: number | null } | null;

/** The preview mode on a patient, or one of its studies, showing an import's predictions or a product's current ones. */
export function previewUrl(
  patientId: string,
  of: { importId: number } | { productId: number },
  studyInstanceUID?: string
): string {
  const query = new URLSearchParams({ patientId });
  if (studyInstanceUID) {
    query.set('studyInstanceUID', studyInstanceUID);
  }
  if ('importId' in of) {
    query.set('predictionImport', String(of.importId));
  } else {
    query.set('predictionProduct', String(of.productId));
  }
  return `/preview?${query}`;
}

/** The Preview links of a patient or a study: the import's predictions, and the product's current ones. */
function PreviewLinks({
  previews,
  patientId,
  studyInstanceUID,
  label,
}: {
  previews: Previews;
  patientId: string;
  studyInstanceUID?: string;
  label: string;
}) {
  if (!previews) {
    return null;
  }
  const link = 'text-xs font-medium text-dl-accent hover:underline';
  return (
    <span className="inline-flex flex-wrap gap-2">
      <a
        className={link}
        href={previewUrl(patientId, { importId: previews.importId }, studyInstanceUID)}
        target="_blank"
        rel="noreferrer"
        aria-label={`Preview ${label} with this import`}
      >
        Preview
      </a>
      {previews.productId != null && (
        <a
          className={link}
          href={previewUrl(patientId, { productId: previews.productId }, studyInstanceUID)}
          target="_blank"
          rel="noreferrer"
          aria-label={`Preview ${label} with the product's current predictions`}
        >
          Product's current
        </a>
      )}
    </span>
  );
}

/** One patient of the tree: opened, its studies, series and images with their predictions counted. */
function PatientRow({
  importId,
  patient,
  previews,
}: {
  importId: number;
  patient: PredictionAdmin.PredictionImportPatient;
  previews: Previews;
}) {
  const [open, setOpen] = useState(false);
  return (
    <li
      aria-label={`Patient ${patient.patientId}`}
      className="border-t border-dl-border py-1"
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className="flex w-full flex-wrap items-center gap-2 text-left text-sm"
      >
        <span aria-hidden>{open ? '▾' : '▸'}</span>
        <span className="break-all font-mono">{patient.patientId}</span>
        <span className="text-dl-muted">
          {patient.studies} studies · {patient.series} series · {patient.images} images ·{' '}
          {patient.predictions} predictions
        </span>
      </button>
      <span className="pl-6">
        <PreviewLinks
          previews={previews}
          patientId={patient.patientId}
          label={`patient ${patient.patientId}`}
        />
      </span>
      {open && (
        <PatientStudies
          importId={importId}
          patientId={patient.patientId}
          previews={previews}
        />
      )}
    </li>
  );
}

function PatientStudies({
  importId,
  patientId,
  previews,
}: {
  importId: number;
  patientId: string;
  previews: Previews;
}) {
  const studies = usePredictionImportStudies(importId, patientId);
  if (studies.isLoading) {
    return <p className="pl-6 text-sm text-dl-muted">Loading its studies…</p>;
  }
  if (studies.isError) {
    return (
      <p
        role="alert"
        className="pl-6 text-sm text-dl-danger"
      >
        Could not load its studies: {(studies.error as Error).message}
      </p>
    );
  }
  return (
    <ul className="pl-6 text-sm">
      {(studies.data ?? []).map(study => (
        <li
          key={study.studyInstanceUID}
          aria-label={`Study ${study.studyInstanceUID}`}
          className="py-1"
        >
          <span className="break-all font-mono">{study.studyInstanceUID}</span>
          <span className="text-dl-muted"> · {study.predictions} predictions </span>
          <PreviewLinks
            previews={previews}
            patientId={patientId}
            studyInstanceUID={study.studyInstanceUID}
            label={`study ${study.studyInstanceUID}`}
          />
          <ul className="pl-6">
            {study.series.map(series => (
              <li
                key={series.seriesInstanceUID}
                aria-label={`Series ${series.seriesInstanceUID}`}
              >
                <span className="break-all font-mono">{series.seriesInstanceUID}</span>
                <span className="text-dl-muted"> · {series.predictions} predictions</span>
                <ul className="pl-6 text-xs text-dl-muted">
                  {series.images.map(image => (
                    <li key={image.sopInstanceUID ?? 'volume'}>
                      <span className="break-all font-mono">
                        {image.sopInstanceUID ?? 'the series as a volume'}
                      </span>{' '}
                      · {image.predictions}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

/** The import's patients, a page at a time, each opened on its studies, series and images. */
function ImportTree({ importId, previews }: { importId: number; previews: Previews }) {
  const [offset, setOffset] = useState(0);
  const page = usePredictionImportPatients(importId, { limit: TREE_PAGE_SIZE, offset });
  if (page.isLoading) {
    return <p className="text-sm text-dl-muted">Loading its patients…</p>;
  }
  if (page.isError) {
    return (
      <p
        role="alert"
        className="text-sm text-dl-danger"
      >
        Could not load its patients: {(page.error as Error).message}
      </p>
    );
  }
  if (!page.data || page.data.total === 0) {
    return <p className="text-sm text-dl-muted">It holds no prediction.</p>;
  }
  return (
    <div>
      <ul aria-label={`Patients of import ${importId}`}>
        {page.data.patients.map(patient => (
          <PatientRow
            key={patient.patientId}
            importId={importId}
            patient={patient}
            previews={previews}
          />
        ))}
      </ul>
      <Pager
        label="patients"
        total={page.data.total}
        limit={TREE_PAGE_SIZE}
        offset={offset}
        onOffset={setOffset}
      />
    </div>
  );
}

/**
 * A file import opened: its figures, what a data manager (create:prediction) may decide — replay, accept, abandon,
 * change product, delete —, what it holds as a patient › study › series › image tree, and the lines it dropped.
 */
export function PredictionImportDetail({
  imported,
  products,
  canDecide,
  canPreview,
  onClose,
}: {
  imported: PredictionImport;
  products: PredictionProduct[];
  canDecide: boolean;
  canPreview: boolean;
  onClose: () => void;
}) {
  const product = products.find(p => p.id === imported.productId);
  const settle = canDecide ? allowedImportActions(imported.status) : [];
  return (
    <Card
      title={`Import ${imported.id} · ${imported.title ?? imported.sourceObject ?? ''}`}
      description={`${product?.name ?? `Product #${imported.productId}`} · requested ${formatDateTime(imported.requestedAt)}${
        imported.finishedAt ? ` · settled ${formatDateTime(imported.finishedAt)}` : ''
      }`}
      actions={<PageButton onClick={onClose}>Close</PageButton>}
    >
      <section
        aria-label={`Import ${imported.id} details`}
        className="flex flex-col gap-4"
      >
        <div className="flex flex-wrap items-center gap-2">
          <ImportStatusBadge status={imported.status} />
          {imported.exportTable && (
            <span className="break-all font-mono text-sm text-dl-muted">
              {imported.exportTable}
            </span>
          )}
        </div>
        <Counts
          counts={[
            { label: 'Lines read', value: imported.linesRead },
            { label: 'Imported', value: imported.predictionsImported },
            { label: 'Dropped', value: imported.predictionsDropped, tone: 'text-dl-warning' },
            { label: 'Replaced', value: imported.predictionsReplaced ?? 0 },
            { label: 'Patients', value: imported.importedPatients },
          ]}
        />
        {imported.failure && (
          <p
            role="alert"
            className="whitespace-pre-wrap break-words rounded-lg bg-dl-danger-tint px-3 py-2 text-sm text-dl-danger"
          >
            {imported.failure}
          </p>
        )}
        {canDecide && (
          <div className="flex flex-wrap items-center gap-2">
            {settle.map(action => (
              <DecisionButton
                key={action}
                importId={imported.id}
                decision={{ type: action }}
                label={SETTLE[action].label}
                tone={SETTLE[action].tone}
                confirm={SETTLE[action].confirm}
              />
            ))}
            {imported.status === 'DONE' && (
              <ChangeProduct
                imported={imported}
                products={products}
              />
            )}
            {imported.status !== 'IMPORTING' && (
              <DecisionButton
                importId={imported.id}
                decision={{ type: 'delete' }}
                label="Delete"
                tone="stop"
                confirm="Delete the import, its predictions and its BigQuery table? Its studies show nothing for the product until the next import."
                onDone={onClose}
              />
            )}
          </div>
        )}
        <div>
          <h3 className="mb-1 text-sm font-medium text-dl-muted">What it holds</h3>
          <ImportTree
            importId={imported.id}
            previews={
              canPreview ? { importId: imported.id, productId: imported.productId ?? null } : null
            }
          />
        </div>
        {imported.predictionsDropped > 0 && (
          <div>
            <h3 className="mb-1 text-sm font-medium text-dl-muted">The lines it dropped</h3>
            <ImportIssues imported={imported} />
          </div>
        )}
      </section>
    </Card>
  );
}
